import { PERMISSIONS } from "@/constants";

import type { NavItemConfig } from "@/types/nav";
import { paths } from "@/paths";

// NOTE: We did not use React Components for Icons, because
//  you may want to get the config from the server.

// NOTE: First level of navItem elements are groups.

export interface DashboardConfig {
	// Overriden by Settings Context.
	layout: "horizontal" | "vertical";
	// Overriden by Settings Context.
	navColor: "blend_in" | "discrete" | "evident";
	navItems: NavItemConfig[];
}

export const dashboardConfig = {
	layout: "vertical",
	navColor: "blend_in",
	navItems: [
		{
			key: "dashboard",
			title: "",
			items: [
				{ key: "dashboard", title: "Dashboard", href: "/", icon: "house", needAccount: true },
				{
					key: "users",
					title: "Users",
					href: "/users",
					icon: "user-three",
					isSuperAdmin: true,
					permission: PERMISSIONS.user,
				},
				{
					key: "accounts",
					title: "Accounts",
					href: "/accounts",
					icon: "users",
					isSuperAdmin: true,
					permission: PERMISSIONS.account,
				},
				{
					// Every account's campaign requests, so no workspace needs selecting.
					key: "client-requests",
					title: "Client Requests",
					href: "/client-requests",
					icon: "kanban",
					isSuperAdmin: true,
				},
				{
					// One card for every client, edited here and shown on the campaign request form.
					key: "rate-card",
					title: "Rate Card",
					href: "/rate-card",
					icon: "receipt",
					isSuperAdmin: true,
				},
				{
					key: "user-management",
					title: "User Management",
					href: "/user-management",
					icon: "user-list",
					needAccount: true,
					permission: PERMISSIONS.management,
				},
				{
					key: "contacts",
					title: "Contacts",
					icon: "user-check",
					needAccount: true,
          permission: PERMISSIONS.contact,
					items: [
						{
							key: "contacts",
							title: "List contacts",
							href: "/contacts",
							needAccount: true,
              permission: PERMISSIONS.contact,
							except: ["/contacts/fields", "/contacts/groups", "/contacts/find-leads"],
						},
						{ key: "contacts:groups", title: "Groups", href: "/contacts/groups", needAccount: true, permission: PERMISSIONS.group },
						// Leads come from the account's own ZoomInfo connection, a plugin only an owner manages.
						{
							key: "contacts:find-leads",
							title: "Find leads",
							href: "/contacts/find-leads",
							needAccount: true,
							permission: PERMISSIONS.plugin,
						},
						{
							key: "contacts:fields",
							title: "Fields",
							href: "/contacts/fields",
							needAccount: true,
							permission: PERMISSIONS.contactField,
						},
					],
				},
				{
					key: "agents",
					title: "Agents",
					href: "/agents",
					icon: "head-circuit",
					needAccount: true,
          isSuperAdmin: true,
					permission: PERMISSIONS.agent,
				},
				{
					// A section rather than a page. Runs is the landing child because you arrive
					// wanting to know whether the agent is good, not to browse the libraries that
					// describe how it was tested.
					key: "proving-ground",
					title: "Proving Ground",
					icon: "target",
					needAccount: true,
					// Gated on published runs rather than on agent configuration, so a manager sees
					// the section at all. What they find inside is Runs and nothing else — the
					// libraries and settings below keep the agent gating they have always had.
					permission: PERMISSIONS.provingRun,
					items: [
						{
							key: "proving-ground:runs",
							title: "Runs",
							href: "/proving-ground/runs",
							needAccount: true,
							permission: PERMISSIONS.provingRun,
						},
						{
							key: "proving-ground:personas",
							title: "Personas",
							href: "/personas",
							needAccount: true,
							isSuperAdmin: true,
							permission: PERMISSIONS.agent,
						},
						{
							key: "proving-ground:scenarios",
							title: "Scenarios",
							href: "/proving-ground/scenarios",
							needAccount: true,
							isSuperAdmin: true,
							permission: PERMISSIONS.agent,
						},
						{
							key: "proving-ground:scorecard",
							title: "Scorecard",
							href: "/proving-ground/scorecard",
							needAccount: true,
							isSuperAdmin: true,
							permission: PERMISSIONS.agent,
						},
						{
							key: "proving-ground:settings",
							title: "Settings",
							href: "/proving-ground/settings",
							needAccount: true,
							isSuperAdmin: true,
							permission: PERMISSIONS.agent,
						},
					],
				},
				{
					key: "campaigns",
					title: "Campaigns",
					href: "/campaigns",
					icon: "megaphone",
					needAccount: true,
					permission: PERMISSIONS.campaign,
				},
				{
					// No `permission`: the API emits none for briefs, and AuthGuard 404s a nav item
					// whose permission is missing from GET /me. The API's policy guards the routes.
					key: "campaign-briefs",
					title: "Campaign Requests",
					href: "/campaign-briefs",
					icon: "calendar-check",
					needAccount: true,
				},
				{
					key: "call-logs",
					title: "Call Logs",
					href: "/call-logs",
					icon: "phone-call",
					needAccount: true,
					permission: PERMISSIONS.call,
				},
        {
					key: "plugins",
					title: "Plugins",
					icon: "puzzle-piece",
					needAccount: true,
          permission: PERMISSIONS.plugin,
					items: [
						{
							key: "plugins",
							title: "List plugins",
							href: "/plugins",
							needAccount: true,
							except: ["/plugins/services"],
              permission: PERMISSIONS.plugin,
						},
						{
							key: "plugins:services",
							title: "Services",
							href: "/plugins/services",
							needAccount: true,
							permission: PERMISSIONS.service,
						},
					],
				},
				{
					key: "phone-numbers",
					title: "Phone Numbers",
					href: "/phone-numbers",
					icon: "phone-number",
					needAccount: true,
					permission: PERMISSIONS.phoneNumber,
          isSuperAdmin: true,
				},
				{
					key: "website-intake",
					title: "Website",
					href: "/settings/website-intake",
					icon: "globe",
					needAccount: true,
					permission: PERMISSIONS.intakeKey,
					except: ["/settings/website-intake/requests"],
				},
				{
					key: "do-not-call",
					title: "Do Not Call",
					href: "/settings/do-not-call",
					icon: "prohibit",
					needAccount: true,
					permission: PERMISSIONS.contact,
				},
				{
					key: "system-logs",
					title: "System Logs",
					href: "/system-logs",
					icon: "file-text",
					needAccount: true,
					permission: PERMISSIONS.systemLog,
          isSuperAdmin: true,
				},
			],
		},
	],
};

export const dashboardConfiga = {
	layout: "vertical",
	navColor: "evident",
	navItems: [
		{
			key: "dashboards",
			title: "Dashboards",
			items: [
				{ key: "overview", title: "Overview", href: paths.dashboard.overview, icon: "house" },
				{ key: "analytics", title: "Analytics", href: paths.dashboard.analytics, icon: "chart-pie" },
				{ key: "ecommerce", title: "E-commerce", href: paths.dashboard.eCommerce, icon: "cube" },
				{ key: "crypto", title: "Crypto", href: paths.dashboard.crypto, icon: "currency-eth" },
			],
		},
		{
			key: "general",
			title: "General",
			items: [
				{
					key: "settings",
					title: "Settings",
					href: paths.dashboard.settings.account,
					icon: "gear",
					matcher: { type: "startsWith", href: "/dashboard/settings" },
				},
				{
					key: "customers",
					title: "Customers",
					icon: "users",
					items: [
						{ key: "customers", title: "List customers", href: paths.dashboard.customers.list },
						{ key: "customers:create", title: "Create customer", href: paths.dashboard.customers.create },
						{ key: "customers:details", title: "Customer details", href: paths.dashboard.customers.details("1") },
					],
				},
				{
					key: "products",
					title: "Products",
					icon: "shopping-bag-open",
					items: [
						{ key: "products", title: "List products", href: paths.dashboard.products.list },
						{ key: "products:create", title: "Create product", href: paths.dashboard.products.create },
						{ key: "products:details", title: "Product details", href: paths.dashboard.products.details("1") },
					],
				},
				{
					key: "orders",
					title: "Orders",
					icon: "shopping-cart-simple",
					items: [
						{ key: "orders", title: "List orders", href: paths.dashboard.orders.list },
						{ key: "orders:create", title: "Create order", href: paths.dashboard.orders.create },
						{ key: "orders:details", title: "Order details", href: paths.dashboard.orders.details("1") },
					],
				},
				{
					key: "invoices",
					title: "Invoices",
					icon: "receipt",
					items: [
						{ key: "invoices", title: "List invoices", href: paths.dashboard.invoices.list },
						{ key: "invoices:create", title: "Create invoice", href: paths.dashboard.invoices.create },
						{ key: "invoices:details", title: "Invoice details", href: paths.dashboard.invoices.details("1") },
					],
				},
				{
					key: "jobs",
					title: "Jobs",
					icon: "read-cv-logo",
					items: [
						{ key: "jobs:browse", title: "Browse jobs", href: paths.dashboard.jobs.browse },
						{ key: "jobs:create", title: "Create job", href: paths.dashboard.jobs.create },
						{
							key: "jobs:company",
							title: "Company details",
							href: paths.dashboard.jobs.companies.overview("1"),
							matcher: { type: "startsWith", href: "/dashboard/jobs/companies/1" },
						},
					],
				},
				{
					key: "logistics",
					title: "Logistics",
					icon: "truck",
					items: [
						{ key: "logistics:metrics", title: "Metrics", href: paths.dashboard.logistics.metrics },
						{ key: "logistics:fleet", title: "Fleet", href: paths.dashboard.logistics.fleet },
					],
				},
				{
					key: "blog",
					title: "Blog",
					icon: "text-align-left",
					items: [
						{ key: "blog", title: "List posts", href: paths.dashboard.blog.list },
						{ key: "blog:create", title: "Create post", href: paths.dashboard.blog.create },
						{ key: "blog:details", title: "Post details", href: paths.dashboard.blog.details("1") },
					],
				},
				{
					key: "social",
					title: "Social",
					icon: "share-network",
					items: [
						{
							key: "social:profile",
							title: "Profile",
							href: paths.dashboard.social.profile.timeline,
							matcher: { type: "startsWith", href: "/dashboard/social/profile" },
						},
						{ key: "social:feed", title: "Feed", href: paths.dashboard.social.feed },
					],
				},
				{
					key: "academy",
					title: "Academy",
					icon: "graduation-cap",
					items: [
						{ key: "academy:browse", title: "Browse courses", href: paths.dashboard.academy.browse },
						{ key: "academy:course", title: "Course details", href: paths.dashboard.academy.details("1") },
					],
				},
				{ key: "file-storage", title: "File storage", href: paths.dashboard.fileStorage, icon: "upload" },
				{
					key: "mail",
					title: "Mail",
					href: paths.dashboard.mail.list("inbox"),
					icon: "envelope-simple",
					matcher: { type: "startsWith", href: "/dashboard/mail" },
				},
				{
					key: "chat",
					title: "Chat",
					href: paths.dashboard.chat.base,
					icon: "chats-circle",
					matcher: { type: "startsWith", href: "/dashboard/chat" },
				},
				{ key: "calendar", title: "Calendar", href: paths.dashboard.calendar, icon: "calendar-check" },
				{ key: "tasks", title: "Tasks", href: paths.dashboard.tasks, icon: "kanban" },
			],
		},
		{
			key: "other",
			title: "Other",
			items: [
				{
					key: "auth",
					title: "Auth",
					icon: "lock",
					items: [
						{
							key: "auth:sign-in",
							title: "Sign in",
							items: [
								{ key: "auth:sign-in:centered", title: "Centered", href: paths.auth.samples.signIn.centered },
								{ key: "auth:sign-in:split", title: "Split", href: paths.auth.samples.signIn.split },
							],
						},
						{
							key: "auth:sign-up",
							title: "Sign up",
							items: [
								{ key: "auth:sign-up:centered", title: "Centered", href: paths.auth.samples.signUp.centered },
								{ key: "auth:sign-up:split", title: "Split", href: paths.auth.samples.signUp.split },
							],
						},
						{
							key: "auth:reset-password",
							title: "Reset password",
							items: [
								{
									key: "auth:reset-password:centered",
									title: "Centered",
									href: paths.auth.samples.resetPassword.centered,
								},
								{ key: "auth:reset-password:split", title: "Split", href: paths.auth.samples.resetPassword.split },
							],
						},
						{
							key: "auth:update-password",
							title: "Update password",
							items: [
								{
									key: "auth:update-password:centered",
									title: "Centered",
									href: paths.auth.samples.updatePassword.centered,
								},
								{ key: "auth:update-password:split", title: "Split", href: paths.auth.samples.updatePassword.split },
							],
						},
						{
							key: "auth:verify-code",
							title: "Verify code",
							items: [
								{ key: "auth:verify-code:centered", title: "Centered", href: paths.auth.samples.verifyCode.centered },
								{ key: "auth:verify-code:split", title: "Split", href: paths.auth.samples.verifyCode.split },
							],
						},
					],
				},
				{ key: "pricing", title: "Pricing", href: paths.pricing, icon: "credit-card" },
				{ key: "checkout", title: "Checkout", href: paths.checkout, icon: "sign-out" },
				{ key: "contact", title: "Contact", href: paths.contact, icon: "address-book" },
				{
					key: "error",
					title: "Error",
					icon: "file-x",
					items: [
						{ key: "error:not-authorized", title: "Not authorized", href: paths.notAuthorized },
						{ key: "error:not-found", title: "Not found", href: paths.notFound },
						{ key: "error:internal-server-error", title: "Internal server error", href: paths.internalServerError },
					],
				},
			],
		},
		{
			key: "misc",
			title: "Misc",
			items: [
				{ key: "i18n", title: "i18n", href: paths.dashboard.i18n, icon: "translate" },
				{
					key: "levels:level-0",
					title: "Level 0",
					icon: "align-left",
					items: [
						{
							key: "levels:level-1a",
							title: "Level 1a",
							items: [
								{
									key: "levels:level-2a",
									title: "Level 2a",
									items: [
										{ key: "levels:level-3a", title: "Level 3a" },
										{ key: "levels:level-3b", title: "Level 3b", disabled: true },
									],
								},
								{ key: "levels:level-2b", title: "Level 2b" },
							],
						},
						{ key: "levels:level-1b", title: "Level 1b" },
					],
				},
				{ key: "disabled", title: "Disabled", disabled: true, icon: "warning-diamond" },
				{ key: "label", title: "Label", icon: "file", label: "New" },
				{ key: "blank", title: "Blank", href: paths.dashboard.blank, icon: "file-dashed" },
				{ key: "external", title: "External link", href: "https://devias.io", external: true, icon: "link" },
			],
		},
	],
} satisfies DashboardConfig;
